// src/pages/UsersPage.js
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../scripts/api";
import Modal from "../components/Modal"; // Assuming you have this from MovesPage

function normalizeUsersResponse(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.rows)) return data.rows;
  return [];
}

function apiErrText(e) {
  const status = e?.response?.status;
  const body = e?.response?.data;
  const msg =
    body?.error ||
    body?.message ||
    (typeof body === "string" ? body : null) ||
    e?.message ||
    "Request failed";
  return status ? `${status}: ${msg}` : msg;
}

function toNumId(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export default function UsersPage() {
  const [rows, setRows] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [drvById, setDrvById] = useState({});
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Form fields for create/edit
  const [form, setForm] = useState({
    email: "",
    password: "",
    display_name: "",
    is_active: true,
    is_superadmin: false,
    driver_id: "",
    // Create-flow extras:
    auto_password: true,          // generate temp password server-side
    send_welcome_email: true,     // email it + force change on first login
    force_password_change: true,
  });

  // After a successful create/reset where a temp password was generated,
  // show it to the admin so they can copy it in case the email bounced.
  const [credResult, setCredResult] = useState(null); // { email, temp_password, welcome_email }

  const sorted = useMemo(() => {
    const copy = [...(rows || [])];
    copy.sort((a, b) => String(a.email || "").localeCompare(String(b.email || "")));
    return copy;
  }, [rows]);

  const load = async () => {
    setBusy(true);
    setMsg("");
    try {
      const { data } = await api.get("/admin/users");
      setRows(normalizeUsersResponse(data));
    } catch (e) {
      setMsg(apiErrText(e));
      setRows([]);
    } finally {
      setBusy(false);
    }
  };

  const loadDrivers = async () => {
    try {
      const { data } = await api.get("/driver");
      const list = Array.isArray(data) ? data : [];
      setDrivers(list);
      const byId = {};
      for (const d of list) {
        byId[d.DriverId] = d;
      }
      setDrvById(byId);
    } catch (e) {
      console.error("Failed to load drivers", e);
    }
  };

  useEffect(() => {
    load();
    loadDrivers();
  }, []);

  const createOrUpdate = async () => {
    const payload = {
      email: String(form.email || "").trim(),
      display_name: String(form.display_name || "").trim(),
      is_active: !!form.is_active,
      is_superadmin: !!form.is_superadmin,
      driver_id: toNumId(form.driver_id) || null,
    };

    if (editMode) {
      // Edit flow: only update the password if admin explicitly typed one.
      if (form.password) payload.password = String(form.password);
    } else {
      // Create flow: admin either types a password OR lets the server
      // generate a temp one. If auto, let the server handle it and
      // opt into the welcome-email + force-change flow.
      if (form.auto_password) {
        payload.send_welcome_email    = !!form.send_welcome_email;
        payload.force_password_change = !!form.force_password_change;
      } else {
        payload.password              = String(form.password || "");
        payload.send_welcome_email    = !!form.send_welcome_email;
        payload.force_password_change = !!form.force_password_change;
        if (!payload.email || !payload.password) {
          setMsg("Email + password required (or tick 'Generate temporary password').");
          return;
        }
      }
      if (!payload.email) {
        setMsg("Email required.");
        return;
      }
    }

    setBusy(true);
    setMsg("");
    try {
      if (editMode) {
        await api.patch(`/admin/users/${selectedUser.id}`, payload);
        setMsg("User updated.");
        closeModal();
      } else {
        const { data } = await api.post("/admin/users", payload);
        if (data?.temp_password) {
          // Show the generated credentials so the admin can copy them even
          // if the email bounces. Modal stays open until they dismiss it.
          setCredResult({
            email:          payload.email,
            temp_password:  data.temp_password,
            welcome_email:  data.welcome_email || null,
            reset:          false,
          });
        } else {
          setMsg("User created.");
          closeModal();
        }
      }
      resetForm();
      await load();
    } catch (e) {
      setMsg(apiErrText(e));
    } finally {
      setBusy(false);
    }
  };

  const resetPassword = async (user) => {
    // Two-step modal via confirm() — first ask whether to generate and email
    // a temp password (default yes), or let the admin pick one by hand.
    const autoChoice = window.confirm(
      `Reset password for ${user.email}?\n\n` +
      `OK  = Generate a temporary password and email it to the user\n` +
      `Cancel = Type a password yourself`
    );

    const payload = { force_password_change: true };
    if (autoChoice) {
      payload.send_welcome_email = true;
    } else {
      const pw = window.prompt("Type the new password (must be ≥ 8 chars):");
      if (!pw) return;
      if (pw.length < 8) { alert("Password must be at least 8 characters."); return; }
      payload.password = pw;
      payload.send_welcome_email = window.confirm(
        "Email the new password to the user as well?"
      );
    }

    setBusy(true);
    setMsg("");
    try {
      const { data } = await api.post(`/admin/users/${user.id}/reset-password`, payload);
      if (data?.temp_password) {
        setCredResult({
          email:          user.email,
          temp_password:  data.temp_password,
          welcome_email:  data.welcome_email || null,
          reset:          true,
        });
      } else {
        setMsg("Password updated.");
      }
    } catch (e) {
      setMsg(apiErrText(e));
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this user?")) return;

    setBusy(true);
    setMsg("");
    try {
      await api.delete(`/admin/users/${id}`);
      await load();
    } catch (e) {
      setMsg(apiErrText(e));
    } finally {
      setBusy(false);
    }
  };

  const openModal = (user = null) => {
    setEditMode(!!user);
    if (user) {
      setForm({
        email: user.email || "",
        password: "",
        display_name: user.display_name || "",
        is_active: String(user.is_active) === "1",
        is_superadmin: String(user.is_superadmin) === "1",
        driver_id: user.driver_id || "",
      });
      setSelectedUser(user);
    } else {
      resetForm();
      setSelectedUser(null);
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedUser(null);
  };

  const resetForm = () => {
    setForm({
      email: "",
      password: "",
      display_name: "",
      is_active: true,
      is_superadmin: false,
      driver_id: "",
      auto_password: true,
      send_welcome_email: true,
      force_password_change: true,
    });
  };

  return (
    <div className="container">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0, fontWeight: 900 }}>Users</h2>
        <button className="btn-outline" onClick={load} disabled={busy}>
          Refresh
        </button>
        <button className="btn" onClick={() => openModal()} disabled={busy}>
          Add User
        </button>
      </div>

      {msg ? (
        <div className="muted" style={{ marginTop: 10, whiteSpace: "pre-wrap" }}>
          {msg}
        </div>
      ) : null}

      <div className="card" style={{ marginTop: 12 }}>
        <div style={{ fontWeight: 900, marginBottom: 10 }}>Existing users</div>
        {busy ? <div className="muted">Loading…</div> : null}

        <div style={{ display: "grid", gap: 10 }}>
          {sorted.map((u) => {
            const active = String(u.is_active) === "1";
            const superadmin = String(u.is_superadmin) === "1";
            const drvId = toNumId(u.driver_id);
            const drvRec = drvId != null ? drvById[drvId] : null;

            return (
              <div key={u.id} className="card" style={{ background: "rgba(255,255,255,0.02)" }}>
                <div className="row" style={{ justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontWeight: 900 }}>
                      {u.email} {superadmin ? <span className="muted">• superadmin</span> : null}
                    </div>
                    <div className="muted" style={{ marginTop: 4 }}>
                      id={u.id} • {u.display_name || "(no name)"} • active={String(u.is_active)} • superadmin={String(u.is_superadmin)} • driver={drvRec ? drvRec.FullName : "(none)"}
                    </div>
                  </div>

                  <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                    <button className="btn-outline" disabled={busy} onClick={() => openModal(u)}>
                      Edit
                    </button>
                    <button className="btn-outline" disabled={busy} onClick={() => resetPassword(u)}>
                      Reset Password
                    </button>
                    <button className="btn-outline" disabled={busy} onClick={() => del(u.id)}>
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          {!busy && sorted.length === 0 ? <div className="muted">No users found.</div> : null}
        </div>
      </div>

      {showModal && (
        <Modal onClose={closeModal}>
          <h2>{editMode ? "Edit User" : "Create User"}</h2>

          <div style={{ display: "grid", gap: 10 }}>
            <input
              className="input"
              placeholder="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <input
              className="input"
              placeholder="display name"
              value={form.display_name}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
            />

            {/* Password section — different UX for create vs edit.
                Create: default to "generate + email" with a collapse-out
                for admins who want to pick the password themselves.
                Edit: blank means "don't touch the password". */}
            {!editMode ? (
              <div className="card" style={{ background: "#f8fafc", padding: 10 }}>
                <label className="row" style={{ gap: 8, alignItems: "flex-start", fontSize: 14 }}>
                  <input
                    type="checkbox"
                    checked={form.auto_password}
                    onChange={(e) => setForm({ ...form, auto_password: e.target.checked })}
                  />
                  <span>
                    <div style={{ fontWeight: 700 }}>Generate temporary password</div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      The server will create a random 12-character password. Recommended.
                    </div>
                  </span>
                </label>
                {!form.auto_password && (
                  <input
                    className="input"
                    style={{ marginTop: 8, width: "100%" }}
                    placeholder="password (min 8 chars)"
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                  />
                )}
                <label className="row" style={{ gap: 8, alignItems: "flex-start", marginTop: 8, fontSize: 14 }}>
                  <input
                    type="checkbox"
                    checked={form.send_welcome_email}
                    onChange={(e) => setForm({ ...form, send_welcome_email: e.target.checked })}
                  />
                  <span>
                    <div style={{ fontWeight: 700 }}>Send welcome email with the password</div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      HTML email with sign-in instructions. Falls back to plain text if HTML is blocked.
                    </div>
                  </span>
                </label>
                <label className="row" style={{ gap: 8, alignItems: "flex-start", marginTop: 8, fontSize: 14 }}>
                  <input
                    type="checkbox"
                    checked={form.force_password_change}
                    onChange={(e) => setForm({ ...form, force_password_change: e.target.checked })}
                  />
                  <span>
                    <div style={{ fontWeight: 700 }}>Force password change on first login</div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      User must pick a new password before they can use the app.
                    </div>
                  </span>
                </label>
              </div>
            ) : (
              <input
                className="input"
                placeholder="new password (leave blank to keep current)"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
            )}

            <label className="row" style={{ gap: 8 }}>
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              />
              Active
            </label>

            <label className="row" style={{ gap: 8 }}>
              <input
                type="checkbox"
                checked={form.is_superadmin}
                onChange={(e) => setForm({ ...form, is_superadmin: e.target.checked })}
              />
              Superadmin
            </label>

            <select className="input" value={form.driver_id} onChange={(e) => setForm({ ...form, driver_id: e.target.value })}>
              <option value="">No Driver</option>
              {drivers.map((drv) => (
                <option key={drv.DriverId} value={drv.DriverId}>
                  {drv.FullName || `Driver #${drv.DriverId}`}
                </option>
              ))}
            </select>

            <button className="btn" onClick={createOrUpdate} disabled={busy}>
              {editMode ? "Update" : "Create"}
            </button>
          </div>

          <button className="btn-outline" onClick={closeModal} disabled={busy}>
            Close
          </button>
        </Modal>
      )}

      {/* Temp-password result — shown after create/reset when the server
          generated a password. Lets the admin copy it in case the welcome
          email failed / bounced / the user has no email yet. */}
      {credResult && (
        <Modal onClose={() => setCredResult(null)}>
          <h2 style={{ marginTop: 0 }}>
            {credResult.reset ? "Password reset" : "User created"}
          </h2>
          <div className="muted" style={{ fontSize: 14, marginBottom: 10 }}>
            A temporary password has been generated for <strong>{credResult.email}</strong>.
            The user will be required to change it on first sign-in.
          </div>

          <div className="card" style={{ background: "#fef3c7", border: "1px solid #fde68a", padding: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase", color: "#92400e" }}>
              Temporary password
            </div>
            <div style={{
              fontFamily: "Consolas, 'Courier New', monospace",
              fontSize: 22, fontWeight: 900, letterSpacing: 1, color: "#7c2d12", marginTop: 6,
              userSelect: "all",
            }}>
              {credResult.temp_password}
            </div>
            <button
              className="btn-outline"
              style={{ marginTop: 10 }}
              onClick={() => navigator.clipboard?.writeText(credResult.temp_password)}
            >
              📋 Copy to clipboard
            </button>
          </div>

          {credResult.welcome_email && (
            <div style={{ marginTop: 12, fontSize: 13 }}>
              {credResult.welcome_email.ok ? (
                <div style={{ color: "#166534" }}>
                  ✓ Welcome email sent to <strong>{credResult.email}</strong>.
                </div>
              ) : (
                <div style={{ color: "#b45309" }}>
                  ⚠ Welcome email not sent ({credResult.welcome_email.reason || "unknown"}).
                  Share the password above manually.
                </div>
              )}
            </div>
          )}

          <div className="row" style={{ justifyContent: "flex-end", marginTop: 14 }}>
            <button className="btn" onClick={() => { setCredResult(null); closeModal(); }}>
              Done
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}